import raw from './email.json'

type Rgb = [number, number, number]

const PRIMARY_DARK: Rgb = [159 / 255, 79 / 255, 67 / 255]
const PRIMARY: Rgb = [201 / 255, 111 / 255, 90 / 255]
const PRIMARY_LIGHT: Rgb = [248 / 255, 212 / 255, 196 / 255]
const PRIMARY_SOFT: Rgb = [251 / 255, 226 / 255, 217 / 255]
const SURFACE_CARD: Rgb = [255 / 255, 253 / 255, 249 / 255]
const SAGE: Rgb = [168 / 255, 191 / 255, 163 / 255]

function mix(a: Rgb, b: Rgb, t: number): Rgb {
    const u = Math.max(0, Math.min(1, t))
    return [a[0] * (1 - u) + b[0] * u, a[1] * (1 - u) + b[1] * u, a[2] * (1 - u) + b[2] * u]
}

function remapSolidFill(r: number, g: number, b: number, a: number): number[] | null {
    if (typeof a !== 'number') return null
    if (Math.abs(a - 1) > 0.02) return null
    // Mattes / masks — leave untouched
    if (r > 0.995 && g < 0.01 && b < 0.01) return null
    // Near white — warm paper
    if (r > 0.96 && g > 0.96 && b > 0.96) return [...SURFACE_CARD, 1]
    // “Success” teal / cyan circles → sage
    if (b > 0.75 && g > 0.42 && r < 0.35) return [...SAGE, a]
    // Blue-dominant (envelope, UI blues) → terracotta ramp by luminance
    if (b > r + 0.05 && b > g + 0.05) {
        const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
        const t = Math.max(0, Math.min(1, (lum - 0.1) / 0.65))
        const rgb = t < 0.42 ? mix(PRIMARY_DARK, PRIMARY, t / 0.42) : mix(PRIMARY, PRIMARY_LIGHT, (t - 0.42) / 0.58)
        return [...rgb, a]
    }
    // Cool light fills (lines, paper tint)
    if (r > 0.78 && g > 0.82 && b > 0.88) return [...PRIMARY_SOFT, a]
    return null
}

function remapColorProp(c: unknown): void {
    if (!c || typeof c !== 'object' || Array.isArray(c)) return
    const co = c as Record<string, unknown>
    if (co.a !== 0 || !Array.isArray(co.k) || co.k.length !== 4) return
    const k = co.k as number[]
    if (!k.every((n) => typeof n === 'number')) return
    const [r, g, b, a] = k
    const next = remapSolidFill(r, g, b, a)
    if (next) co.k = next
}

function remapNode(val: unknown): void {
    if (val === null || typeof val !== 'object') return
    if (Array.isArray(val)) {
        for (const item of val) remapNode(item)
        return
    }
    const o = val as Record<string, unknown>
    if ('c' in o) remapColorProp(o.c)

    for (const [key, child] of Object.entries(o)) {
        if (key === 'v' && o.ty === 2 && o.nm === 'Color') remapColorProp(child)
        else remapNode(child)
    }
}

function clone<T>(x: T): T {
    return JSON.parse(JSON.stringify(x)) as T
}

const cloned = clone(raw as object)
remapNode(cloned)

/** Magic-link “email sent” animation recoloured to Eltrust terracotta / sage / warm paper. */
export const emailLottieEltrustData = cloned
